/**
 * BattleStateManager - Singleton class to manage battle state across scenes
 * 
 * This class provides a centralized way to store and manage battle configuration,
 * player lists, and other battle-related data that needs to persist across
 * different Phaser scenes during the battle flow.
 * 
 * @class BattleStateManager
 */

import { limitInventoryPerContainer } from './PlayerSpawner.js';

const toFiniteNumber = (value, fallback) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const buildInventoryKey = (itemId, equipmentSlot, slotNumber) => {
    return `${itemId ?? ''}|${equipmentSlot ?? ''}|${slotNumber ?? ''}`;
};

const buildNormalizationContext = (previous = []) => {
    const byId = new Map();
    const byKey = new Map();
    if (Array.isArray(previous)) {
        previous.forEach((entry) => {
            if (!entry) return;
            const inventoryId = entry.inventoryId ?? entry.id ?? null;
            if (inventoryId != null && !byId.has(inventoryId)) {
                byId.set(inventoryId, entry);
            }
            const key = buildInventoryKey(
                entry.itemId ?? entry.item_id ?? null,
                entry.equipmentSlot ?? entry.equipment_slot ?? null,
                entry.slotNumber ?? entry.slot_number ?? null
            );
            if (!byKey.has(key)) {
                byKey.set(key, entry);
            }
        });
    }
    return { byId, byKey };
};

const normalizeInventoryEntry = (entry, context = null) => {
    if (!entry) return null;

    const normalized = { ...entry };
    let effect = normalized.effect ?? normalized.itemEffect ?? normalized.effect_json ?? null;
    if (effect && typeof effect === 'string') {
        try {
            effect = JSON.parse(effect);
        } catch (_) {
            // leave as string if parsing fails
        }
    }

    const itemId = normalized.itemId ?? normalized.item_id ?? null;
    normalized.itemId = itemId;
    normalized.item_id = itemId;

    const itemType = normalized.itemType ?? normalized.item_type ?? null;
    normalized.itemType = itemType;
    normalized.item_type = itemType;

    const equipmentSlot = normalized.equipmentSlot ?? normalized.equipment_slot ?? null;
    normalized.equipmentSlot = equipmentSlot;
    normalized.equipment_slot = equipmentSlot;

    const slotNumber = normalized.slotNumber ?? normalized.slot_number ?? null;
    normalized.slotNumber = slotNumber;
    normalized.slot_number = slotNumber;

    const iconKey = normalized.iconKey ?? normalized.icon_key ?? null;
    normalized.iconKey = iconKey;
    normalized.icon_key = iconKey;

    const maxStack = normalized.maxStack ?? normalized.max_stack ?? null;
    normalized.maxStack = maxStack;
    normalized.max_stack = maxStack;

    const themeId = normalized.themeId ?? normalized.theme_id ?? null;
    normalized.themeId = themeId;
    normalized.theme_id = themeId;

    const quantity = Math.max(0, toFiniteNumber(normalized.quantity ?? normalized.qty ?? normalized.amount ?? 0, 0));
    normalized.quantity = quantity;
    normalized.qty = quantity;

    const inventoryId = normalized.inventoryId ?? normalized.id ?? null;
    normalized.inventoryId = inventoryId;
    if (inventoryId != null) {
        normalized.id = inventoryId;
    }

    let previous = null;
    if (context) {
        if (inventoryId != null && context.byId.has(inventoryId)) {
            previous = context.byId.get(inventoryId);
            context.byId.delete(inventoryId);
        } else {
            const key = buildInventoryKey(itemId, equipmentSlot, slotNumber);
            if (context.byKey.has(key)) {
                previous = context.byKey.get(key);
                context.byKey.delete(key);
            }
        }
    }

    const initialQty = Math.max(0, toFiniteNumber(previous?.initialQty ?? normalized.initialQty, quantity));
    const originalQty = Math.max(0, toFiniteNumber(previous?.originalQty ?? normalized.originalQty, quantity));
    const lootQty = Math.max(0, toFiniteNumber(previous?.lootQty ?? normalized.lootQty, 0));
    const consumedOriginal = Math.max(0, toFiniteNumber(previous?.consumedOriginal ?? normalized.consumedOriginal, 0));
    const consumedLoot = Math.max(0, toFiniteNumber(previous?.consumedLoot ?? normalized.consumedLoot, 0));

    normalized.initialQty = initialQty;
    normalized.originalQty = originalQty;
    normalized.lootQty = lootQty;
    normalized.consumedOriginal = consumedOriginal;
    normalized.consumedLoot = consumedLoot;
    normalized.effect = effect;

    return normalized;
};

const normalizeInventoryList = (inventory, previousInventory = []) => {
    const context = buildNormalizationContext(previousInventory);
    if (!Array.isArray(inventory)) {
        return [];
    }
    return inventory
        .map((entry) => normalizeInventoryEntry(entry, context))
        .filter(Boolean);
};

class BattleStateManager {
    MAX_PLAYERS = 100;
    constructor() {
        if (BattleStateManager.instance) {
            return BattleStateManager.instance;
        }

        // Battle configuration
        this.maxPlayers = this.MAX_PLAYERS;
        this.gameMode = 'free-for-all';
        this.teamCount = 2;
        this.maxBattleStack = 10; // Default consumable stack size
        
        // Player lists
        this.joinedPlayers = [];
        this.aiPlayers = [];
        this.finalPlayerList = [];
        
        // Battlefield and assets
        this.battlefield = null;
        this.battlefieldInstanceId = null;
        this.battlefieldCustomName = null;
        this.requiredTextures = [];
        
        // Battle results
        this.battleResults = null;
        this.winners = [];
        this.battleRewards = null;
        this.rewardsScript = null;
        this.prizePoolConfig = null;
        
        // Vote system
        this.voteSessionId = null;
        this.battleId = null;
        this.sessionId = null;
        
        // Loading state
        this.assetsLoaded = false;
        this.loadingProgress = 0;

        BattleStateManager.instance = this;
    }

    /**
     * Reset all battle state to initial values
     */
    reset() {
        this.maxPlayers = this.MAX_PLAYERS;
        this.gameMode = 'free-for-all';
        this.teamCount = 2;
        this.maxBattleStack = 10;
        this.joinedPlayers = [];
        this.aiPlayers = [];
        this.finalPlayerList = [];
        this.battlefield = null;
        this.battlefieldInstanceId = null;
        this.battlefieldCustomName = null;
        this.requiredTextures = [];
        this.battleResults = null;
        this.winners = [];
        this.battleRewards = null;
        this.rewardsScript = null;
        this.prizePoolConfig = null;
        this.voteSessionId = null;
        this.battleId = null;
        this.sessionId = null;
        this.assetsLoaded = false;
        this.loadingProgress = 0;
    }

    /**
     * Set battle configuration
     * @param {Object} config - Battle configuration object
     * @param {number} config.maxPlayers - Maximum number of players
     * @param {string} config.gameMode - Game mode (free-for-all, teams, etc.)
     * @param {number} config.teamCount - Number of teams (for team modes)
     */
    setBattleConfig(config) {
        this.maxPlayers = config.maxPlayers || this.MAX_PLAYERS;
        this.gameMode = config.gameMode || 'free-for-all';
        this.teamCount = config.teamCount || 2;
        // Use nullish coalescing to allow 0 as a valid value
        this.maxBattleStack = config.maxBattleStack !== undefined ? config.maxBattleStack : (this.maxBattleStack ?? 10);
        // Store full config for access by other systems (e.g., stat normalization)
        this.battleConfig = config;
        if (config.prizePoolConfig) {
            this.setPrizePoolConfig(config.prizePoolConfig);
        } else if (config.prizePool) {
            this.setPrizePoolConfig(config.prizePool);
        }
    }

    /**
     * Add a player to the joined players list
     * @param {Object} player - Player object with wallet, hero data, etc.
     */
    addJoinedPlayer(player) {
        if (!player) {
            return;
        }

        const exists = this.joinedPlayers.find(p => {
            if (player.userId && p.userId) {
                return p.userId === player.userId;
            }
            if (player.wallet && p.wallet) {
                return p.wallet === player.wallet;
            }
            return false;
        });

        if (!exists) {
            this.joinedPlayers.push(player);
        }
    }

    /**
     * Remove a player from the joined players list
     * @param {string} wallet - Wallet address of player to remove
     */
    removeJoinedPlayer(identifier) {
        if (identifier == null) {
            return;
        }

        const isObject = typeof identifier === 'object';
        const targetUserId = isObject ? identifier.userId : identifier;
        const targetWallet = isObject ? identifier.wallet : identifier;

        this.joinedPlayers = this.joinedPlayers.filter(p => {
            const matchesUser = targetUserId != null && p.userId != null && p.userId === targetUserId;
            const matchesWallet = targetWallet != null && p.wallet != null && p.wallet === targetWallet;
            return !(matchesUser || matchesWallet);
        });
    }

    /**
     * Set the AI players list
     * @param {Array} aiPlayers - Array of AI player objects
     */
    setAIPlayers(aiPlayers) {
        this.aiPlayers = aiPlayers;
    }

    /**
     * Set the final combined player list
     * @param {Array} players - Array of all players (joined + AI)
     */
    setFinalPlayerList(players) {
        this.finalPlayerList = players;
    }

    setPlayerInventory(userId, inventory) {
        if (userId == null) {
            return;
        }

        const applyInventory = (player, reuseReference = false) => {
            if (!player) return;
            const hero = player.hero = player.hero || {};
            const previousInventory = Array.isArray(hero.inventory) ? hero.inventory : [];
            const normalized = normalizeInventoryList(inventory, previousInventory);
            if (reuseReference && Array.isArray(hero.inventory)) {
                hero.inventory.length = 0;
                normalized.forEach(entry => hero.inventory.push({ ...entry }));
            } else {
                hero.inventory = normalized.map(entry => ({ ...entry }));
            }
            player.inventory = hero.inventory;
        };

        const joined = this.joinedPlayers.find(p => p && p.userId === userId);
        if (joined) {
            applyInventory(joined, true);
        }

        if (Array.isArray(this.finalPlayerList) && this.finalPlayerList.length) {
            this.finalPlayerList.forEach(p => {
                if (p && p.userId === userId && p !== joined) {
                    applyInventory(p, false);
                }
            });
        }
    }

    setHeroInventory(heroId, inventory) {
        if (!heroId) {
            return;
        }

        // Try to get itemsById from first player's scene (for inventory limiting)
        let itemsById = null;
        const allPlayers = [...(this.joinedPlayers || []), ...(this.finalPlayerList || []), ...(this.aiPlayers || [])];
        const firstPlayer = allPlayers.find(p => p?.hero?.id === heroId);
        if (firstPlayer?.scene?.itemsById) {
            itemsById = firstPlayer.scene.itemsById;
        }

        const applyInventory = (player, reuseReference = false) => {
            if (!player) return;
            const hero = player.hero = player.hero || {};
            hero.id = hero.id || heroId;
            const previousInventory = Array.isArray(hero.inventory) ? hero.inventory : [];
            const normalized = normalizeInventoryList(inventory, previousInventory);
            
            // Limit to maxBattleStack items per container per match (if itemsById is available)
            let limitedInventory = normalized;
            if (itemsById || player.scene?.itemsById) {
                try {
                    // Use nullish coalescing to allow 0 as a valid value
                    const maxStack = this.maxBattleStack ?? 10;
                    limitedInventory = limitInventoryPerContainer(
                        normalized,
                        itemsById || player.scene.itemsById,
                        maxStack
                    );
                } catch (err) {
                    console.warn('[BattleStateManager] Failed to limit inventory:', err);
                    limitedInventory = normalized;
                }
            }
            
            if (reuseReference && Array.isArray(hero.inventory)) {
                hero.inventory.length = 0;
                limitedInventory.forEach(entry => hero.inventory.push({ ...entry }));
            } else {
                hero.inventory = limitedInventory.map(entry => ({ ...entry }));
            }
            player.inventory = hero.inventory;
        };

        const updateCollection = (collection, reuseReference = false) => {
            if (!Array.isArray(collection)) return;
            collection.forEach(player => {
                if (player?.hero?.id === heroId) {
                    applyInventory(player, reuseReference);
                }
            });
        };

        updateCollection(this.joinedPlayers, true);
        updateCollection(this.finalPlayerList, false);
        updateCollection(this.aiPlayers, false);
    }

    /**
     * Set the battlefield for the battle
     * @param {Object} battlefield - Battlefield object with image, name, etc.
     * @param {string} battlefieldInstanceId - Battlefield instance ID (optional)
     * @param {string} battlefieldCustomName - Custom battlefield name (optional)
     */
    setBattlefield(battlefield, battlefieldInstanceId = null, battlefieldCustomName = null) {
        this.battlefield = battlefield;
        this.battlefieldInstanceId = battlefieldInstanceId;
        this.battlefieldCustomName = battlefieldCustomName;
    }

    /**
     * Get the battlefield display name (custom name if available, otherwise template name)
     * @returns {string} Battlefield display name
     */
    getBattlefieldDisplayName() {
        return this.battlefieldCustomName || this.battlefield?.name || 'Unknown Battlefield';
    }

    /**
     * Set the battlefield instance ID
     * @param {string} battlefieldInstanceId - Battlefield instance ID
     */
    setBattlefieldInstanceId(battlefieldInstanceId) {
        this.battlefieldInstanceId = battlefieldInstanceId;
    }

    /**
     * Get the battlefield instance ID
     * @returns {string|null} Battlefield instance ID
     */
    getBattlefieldInstanceId() {
        return this.battlefieldInstanceId;
    }

    /**
     * Add required textures for asset loading
     * @param {Array} textures - Array of texture keys needed
     */
    addRequiredTextures(textures) {
        this.requiredTextures = [...new Set([...this.requiredTextures, ...textures])];
    }

    /**
     * Get the complete player list for the battle
     * @returns {Array} Array of all players
     */
    getPlayerList() {
        return this.finalPlayerList;
    }

    /**
     * Get joined players only
     * @returns {Array} Array of joined players
     */
    getJoinedPlayers() {
        return this.joinedPlayers;
    }

    /**
     * Get AI players only
     * @returns {Array} Array of AI players
     */
    getAIPlayers() {
        return this.aiPlayers;
    }

    /**
     * Get current battle configuration
     * @returns {Object} Battle configuration object
     */
    getBattleConfig() {
        // Return full battle config if available, otherwise return basic config
        if (this.battleConfig) {
            return this.battleConfig;
        }
        return {
            maxPlayers: this.maxPlayers,
            gameMode: this.gameMode,
            teamCount: this.teamCount,
            prizePoolConfig: this.prizePoolConfig
        };
    }

    setPrizePoolConfig(config) {
        if (!config) {
            this.prizePoolConfig = null;
            return;
        }

        try {
            this.prizePoolConfig = JSON.parse(JSON.stringify(config));
        } catch (_) {
            this.prizePoolConfig = Array.isArray(config?.tokens) ? { tokens: [...config.tokens] } : { tokens: [] };
        }
    }

    getPrizePoolConfig() {
        if (!this.prizePoolConfig) return null;
        try {
            return JSON.parse(JSON.stringify(this.prizePoolConfig));
        } catch (_) {
            return this.prizePoolConfig;
        }
    }

    /**
     * Get the selected battlefield
     * @returns {Object} Battlefield object
     */
    getBattlefield() {
        return this.battlefield;
    }

    /**
     * Get required textures for loading
     * @returns {Array} Array of texture keys
     */
    getRequiredTextures() {
        return this.requiredTextures;
    }

    /**
     * Set battle results
     * @param {Object} results - Battle results object
     */
    setBattleResults(results) {
        this.battleResults = results;
    }

    setBattleRewards(rewards) {
        this.battleRewards = rewards;
    }

    setRewardsScript(script) {
        this.rewardsScript = script;
    }

    /**
     * Set battle winners
     * @param {Array} winners - Array of winner objects
     */
    setWinners(winners) {
        this.winners = winners;
    }

    /**
     * Get battle results
     * @returns {Object} Battle results object
     */
    getBattleResults() {
        return this.battleResults;
    }

    getBattleRewards() {
        return this.battleRewards;
    }

    getRewardsScript() {
        return this.rewardsScript;
    }

    /**
     * Get battle winners
     * @returns {Array} Array of winner objects
     */
    getWinners() {
        return this.winners;
    }

    /**
     * Set asset loading progress
     * @param {number} progress - Progress value (0-100)
     */
    setLoadingProgress(progress) {
        this.loadingProgress = Math.max(0, Math.min(100, progress));
    }

    /**
     * Get asset loading progress
     * @returns {number} Progress value (0-100)
     */
    getLoadingProgress() {
        return this.loadingProgress;
    }

    /**
     * Mark assets as loaded
     * @param {boolean} loaded - Whether assets are loaded
     */
    setAssetsLoaded(loaded) {
        this.assetsLoaded = loaded;
    }

    /**
     * Check if assets are loaded
     * @returns {boolean} Whether assets are loaded
     */
    areAssetsLoaded() {
        return this.assetsLoaded;
    }

    /**
     * Get total number of players (joined + AI)
     * @returns {number} Total player count
     */
    getTotalPlayerCount() {
        return this.joinedPlayers.length + this.aiPlayers.length;
    }

    /**
     * Check if battle is ready to start
     * @returns {boolean} Whether battle is ready
     */
    isBattleReady() {
        return this.finalPlayerList.length > 0 && 
               this.battlefield !== null && 
               this.assetsLoaded;
    }

    /**
     * Find a player by userId
     * @param {number} userId - User ID to search for
     * @returns {Object|null} Player object or null if not found
     */
    findPlayerByUserId(userId) {
        try {
            // Search in final player list first (most complete)
            let player = this.finalPlayerList?.find(p => p && p.userId === userId);
            if (player) return player;
            
            // Fallback to joined players
            player = this.joinedPlayers?.find(p => p && p.userId === userId);
            if (player) return player;
            
            // Fallback to AI players
            player = this.aiPlayers?.find(p => p && p.userId === userId);
            return player || null;
        } catch (_) {
            return null;
        }
    }

    /**
     * Find a player by wallet address
     * @param {string} wallet - Wallet address to search for
     * @returns {Object|null} Player object or null if not found
     */
    findPlayerByWallet(wallet) {
        try {
            // Search in final player list first (most complete)
            let player = this.finalPlayerList?.find(p => p && (p.wallet === wallet || p.walletAddress === wallet));
            if (player) return player;
            
            // Fallback to joined players
            player = this.joinedPlayers?.find(p => p && (p.wallet === wallet || p.walletAddress === wallet));
            if (player) return player;
            
            // Fallback to AI players
            player = this.aiPlayers?.find(p => p && (p.wallet === wallet || p.walletAddress === wallet));
            return player || null;
        } catch (_) {
            return null;
        }
    }

    findPlayerByHeroId(heroId) {
        try {
            if (!heroId) return null;

            let player = this.finalPlayerList?.find(p => p?.hero?.id === heroId);
            if (player) return player;

            player = this.joinedPlayers?.find(p => p?.hero?.id === heroId);
            if (player) return player;

            player = this.aiPlayers?.find(p => p?.hero?.id === heroId);
            return player || null;
        } catch (_) {
            return null;
        }
    }

    /**
     * Set vote session ID
     * @param {string} sessionId - Vote session ID
     */
    setVoteSessionId(sessionId) {
        this.voteSessionId = sessionId;
    }

    /**
     * Get vote session ID
     * @returns {string} Vote session ID
     */
    getVoteSessionId() {
        return this.voteSessionId;
    }

    /**
     * Set battle ID
     * @param {string} battleId - Battle ID
     */
    setBattleId(battleId) {
        this.battleId = battleId;
    }

    /**
     * Get battle ID
     * @returns {string} Battle ID
     */
    getBattleId() {
        return this.battleId;
    }

    /**
     * Set game session ID
     * @param {string} sessionId - Game session ID
     */
    setSessionId(sessionId) {
        this.sessionId = sessionId;
    }

    /**
     * Get game session ID
     * @returns {string} Game session ID
     */
    getSessionId() {
        return this.sessionId;
    }

    /**
     * Get debug information about current state
     * @returns {Object} Debug information
     */
    getDebugInfo() {
        return {
            maxPlayers: this.maxPlayers,
            gameMode: this.gameMode,
            teamCount: this.teamCount,
            joinedPlayers: this.joinedPlayers.length,
            aiPlayers: this.aiPlayers.length,
            totalPlayers: this.getTotalPlayerCount(),
            battlefield: this.battlefield ? this.battlefield.name : 'None',
            requiredTextures: this.requiredTextures.length,
            assetsLoaded: this.assetsLoaded,
            loadingProgress: this.loadingProgress,
            battleReady: this.isBattleReady(),
            voteSessionId: this.voteSessionId,
            battleId: this.battleId,
            sessionId: this.sessionId
        };
    }
}

// Export singleton instance
const battleStateManager = new BattleStateManager();
export default battleStateManager;
